import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
  Autocomplete,
  TextField,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAdmissions } from '../../../hooks/use-admissions';
import { CreateAdmissionRequest } from '../../../lib/api/admissions.api';

interface CreateAdmissionDialogProps {
  open: boolean;
  onClose: () => void;
  personId?: string | undefined;
  initialData?:
    | {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
      }
    | undefined;
}

interface Option {
  id: string;
  label: string;
}

export function CreateAdmissionDialog({
  open,
  onClose,
  personId,
  initialData,
}: CreateAdmissionDialogProps) {
  const { currentTenantId } = useTenantStore();
  const { createAdmission, isCreating } = useAdmissions();

  // Person data (inline creation — no pre-existing person needed)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Sync state when dialog opens or data changes
  useEffect(() => {
    if (open) {
      setFirstName(initialData?.firstName || '');
      setLastName(initialData?.lastName || '');
      setEmail(initialData?.email || '');
      setPhone(initialData?.phone || '');
    }
  }, [open, initialData]);

  // Dropdowns
  const [progId, setProgId] = useState<Option | null>(null);
  const [tId, setTId] = useState<Option | null>(null);
  const [programs, setPrograms] = useState<Option[]>([]);
  const [terms, setTerms] = useState<Option[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch dropdown data when the dialog opens
  useEffect(() => {
    if (!open || !currentTenantId) return;

    let isMounted = true;
    setLoadingData(true);
    setError(null);

    Promise.all([
      getDocs(
        query(collection(db, `tenants/${currentTenantId}/programs`), orderBy('name'), limit(100)),
      ),
      getDocs(
        query(
          collection(db, `tenants/${currentTenantId}/terms`),
          orderBy('startDate', 'desc'),
          limit(100),
        ),
      ),
    ])
      .then(([programsSnap, termsSnap]) => {
        if (!isMounted) return;
        setPrograms(programsSnap.docs.map((d) => ({ id: d.id, label: d.data().name || d.id })));
        setTerms(termsSnap.docs.map((d) => ({ id: d.id, label: d.data().name || d.id })));
        setLoadingData(false);
      })
      .catch((err) => {
        if (isMounted) {
          setLoadingData(false);
          setError('Error al cargar programas y períodos.');
          console.error(err);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [open, currentTenantId]);

  const handleClose = () => {
    if (!personId && !initialData) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
    }
    setProgId(null);
    setTId(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId || !progId || !tId) {
      setError('Por favor, selecciona un programa y un período.');
      return;
    }

    if (!personId && (!firstName.trim() || !lastName.trim() || !email.trim())) {
      setError('Por favor, completa nombre, apellido y email.');
      return;
    }

    setError(null);
    try {
      const payload: CreateAdmissionRequest = {
        tenantId: currentTenantId,
        programId: progId.id,
        termId: tId.id,
      };

      if (personId) {
        payload.personId = personId;
      } else {
        payload.firstName = firstName.trim();
        payload.lastName = lastName.trim();
        payload.email = email.trim();
        if (phone.trim()) payload.phone = phone.trim();
      }

      await createAdmission(payload);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Error creando admisión');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>Nueva Admisión</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Datos del Postulante
            </Typography>

            {loadingData ? (
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" py={4}>
                <CircularProgress size={24} />
                <span>Cargando...</span>
              </Stack>
            ) : (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Nombre(s)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    fullWidth
                    autoFocus={!personId}
                    disabled={!!personId || !!initialData}
                  />
                  <TextField
                    label="Apellido(s)"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    fullWidth
                    disabled={!!personId || !!initialData}
                  />
                </Stack>

                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  helperText={
                    personId
                      ? 'Vinculado a persona existente'
                      : 'Se usará para buscar o crear la Persona en el sistema'
                  }
                  disabled={!!personId || !!initialData}
                />

                <TextField
                  label="Teléfono (opcional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                  disabled={!!personId || !!initialData}
                />

                <Divider />

                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  Programa y Período
                </Typography>

                <Autocomplete
                  options={programs}
                  value={progId}
                  onChange={(_, val) => setProgId(val)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...(params as any)} label="Programa Académico" required />
                  )}
                  noOptionsText="Sin programas disponibles"
                />

                <Autocomplete
                  options={terms}
                  value={tId}
                  onChange={(_, val) => setTId(val)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...(params as any)} label="Período de Ingreso" required />
                  )}
                  noOptionsText="Sin períodos disponibles"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={isCreating || loadingData}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              isCreating ||
              loadingData ||
              !firstName.trim() ||
              !lastName.trim() ||
              !email.trim() ||
              !progId ||
              !tId
            }
            sx={{ minWidth: 140 }}
          >
            {isCreating ? 'Guardando...' : 'Crear Admisión'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
